import { useEffect, useRef, useState } from "react";
import { Award, Pencil, Search, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const inputCls = "input-lux";
const labelCls = "block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide";

export default function AdminCertificates() {
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // cert overview object
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/certificates/overview").then((res) => setOverview(res.data)).catch(() => {});
    api.get(`/admin/certificates/records${search ? `?search=${encodeURIComponent(search)}` : ""}`).then((res) => setRecords(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/certificates/records${search ? `?search=${encodeURIComponent(search)}` : ""}`).then((res) => setRecords(res.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const openEdit = (cert) => {
    setEditing(cert);
    setForm({
      recognition_text: cert.recognition_text || "",
      caption: cert.social?.caption || "",
      hashtags: cert.social?.hashtags || "",
      instructions: cert.social?.instructions || "",
      contest: cert.social?.contest || "",
      campaign_enabled: cert.social?.campaign_enabled ?? true,
    });
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/certificates/${editing.code}/config`, form);
      toast.success("Certificate content updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const uploadTemplate = async (code, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post(`/admin/certificates/${code}/template`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="admin-certificates-page">
      <div>
        <p className="eyebrow-label">Administration</p>
        <h1 className="page-title mt-2">Certificate Management</h1>
        <p className="page-sub mt-1">
          Certificates unlock automatically from the rank system — no approval needed. Manage templates, recognition text, and per-rank social campaigns here.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="cert-overview-grid">
        {(overview?.certificates || []).map((cert) => (
          <div key={cert.code} className="card-lux p-5 space-y-3" data-testid={`cert-overview-${cert.code}`}>
            <div className="flex items-center justify-between">
              <Award className="h-4 w-4 text-[#D4AF37]" />
              <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${cert.social?.campaign_enabled ? "text-emerald-400 border-emerald-400/30" : "text-[#94A3B8] border-[#222530]"}`} data-testid={`cert-campaign-${cert.code}`}>
                {cert.social?.campaign_enabled ? "Campaign On" : "Campaign Off"}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{cert.rank_name}</p>
              <p className="text-[10px] font-mono text-[#64748B] mt-0.5">PM-{cert.code}-* · Rank level {cert.level}</p>
            </div>
            <p className="text-2xl font-bold text-[#D4AF37] font-mono" data-testid={`cert-earned-${cert.code}`}>{cert.earned_count}</p>
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] -mt-2">members earned</p>
            <button onClick={() => openEdit(cert)} className="btn-gold-outline w-full py-2 text-[11px] flex items-center justify-center gap-1.5" data-testid={`cert-edit-${cert.code}`}>
              <Pencil className="h-3 w-3" /> Manage
            </button>
          </div>
        ))}
      </div>

      <section className="space-y-4" data-testid="cert-records-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow-label">Earned Certificates</p>
            <p className="mt-1 text-xs text-[#64748B]">Total issued: <span className="text-[#D4AF37] font-mono" data-testid="cert-total-earned">{overview?.total_earned ?? "—"}</span></p>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="input-lux pl-9 w-72" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search member, UID or Certificate ID…" data-testid="cert-records-search" />
          </div>
        </div>
        <div className="card-lux overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cert-records-table">
              <thead>
                <tr className="border-b border-[#222530] text-left">
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Certificate ID</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Rank</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Achieved</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Recorded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#222530]">
                {records.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-[#64748B]" data-testid="cert-records-empty">No certificates earned yet.</td></tr>
                ) : (
                  records.map((r) => (
                    <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`cert-record-${r.certificate_id}`}>
                      <td className="px-5 py-4 font-mono text-xs text-[#D4AF37]">{r.certificate_id}</td>
                      <td className="px-5 py-4">
                        <p className="text-white font-medium">{r.member_name}</p>
                        <p className="text-[10px] text-[#64748B] font-mono">{r.member_uid}</p>
                      </td>
                      <td className="px-5 py-4 text-xs text-[#94A3B8]">{r.rank_name}</td>
                      <td className="px-5 py-4 text-xs text-[#94A3B8] font-mono hidden md:table-cell">{fmtMYT(r.achieved_at)}</td>
                      <td className="px-5 py-4 text-xs text-[#64748B] font-mono hidden lg:table-cell">{fmtMYT(r.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {editing && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative card-lux w-full max-w-xl max-h-[85vh] overflow-y-auto p-6 space-y-5" data-testid="cert-edit-modal">
            <div className="flex items-center justify-between">
              <div>
                <p className="eyebrow-label">Manage Certificate</p>
                <h3 className="font-heading text-lg font-semibold text-white mt-1">{editing.rank_name}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#94A3B8] hover:text-white p-1" data-testid="cert-edit-close" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div>
              <label className={labelCls}>Recognition Text <span className="text-[#64748B]">(reference copy — the official wording is part of the final master template)</span></label>
              <textarea className={`${inputCls} min-h-[90px]`} value={form.recognition_text} onChange={(e) => setForm((f) => ({ ...f, recognition_text: e.target.value }))} data-testid="cert-recognition-input" />
            </div>

            <div className="border border-[#222530] rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Share Your Achievement Campaign</p>
                <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                  <input type="checkbox" checked={form.campaign_enabled} onChange={(e) => setForm((f) => ({ ...f, campaign_enabled: e.target.checked }))} className="accent-[#D4AF37]" data-testid="cert-campaign-toggle" />
                  Enabled
                </label>
              </div>
              <div>
                <label className={labelCls}>Social Caption</label>
                <textarea className={`${inputCls} min-h-[60px]`} value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid="cert-caption-input" />
              </div>
              <div>
                <label className={labelCls}>Hashtags</label>
                <input className={inputCls} value={form.hashtags} onChange={(e) => setForm((f) => ({ ...f, hashtags: e.target.value }))} data-testid="cert-hashtags-input" />
              </div>
              <div>
                <label className={labelCls}>Posting Instructions</label>
                <textarea className={`${inputCls} min-h-[60px]`} value={form.instructions} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} data-testid="cert-instructions-input" />
              </div>
              <div>
                <label className={labelCls}>Contest Instructions</label>
                <textarea className={`${inputCls} min-h-[60px]`} value={form.contest} onChange={(e) => setForm((f) => ({ ...f, contest: e.target.value }))} data-testid="cert-contest-input" />
              </div>
            </div>

            <div>
              <label className={labelCls}>Certificate Template <span className="text-[#64748B]">(PNG/JPG — re-renders existing certificates on next view)</span></label>
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadTemplate(editing.code, e.target.files?.[0])} data-testid="cert-template-input" />
                <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-gold-outline px-4 py-2 text-[11px] flex items-center gap-2 disabled:opacity-50" data-testid="cert-template-upload-button">
                  <Upload className="h-3.5 w-3.5" /> Upload New Template
                </button>
                <span className="text-[10px] font-mono text-[#64748B]">template v{editing.template_version}</span>
              </div>
            </div>

            <button onClick={saveConfig} disabled={busy} className="btn-gold w-full py-2.5 text-xs disabled:opacity-50" data-testid="cert-save-button">
              {busy ? "Saving…" : "Save Certificate Settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
